import { useState } from 'react';
import type { SchoolLevel } from '@jeraisonne/db';
import { readStoredPlayerLevel, storePlayerLevel } from '@/lib/player/overview';
import { useAuth } from '@/lib/auth';
import { useRoleNewsFeed } from '@/lib/news/use-role-news-feed';
import { useFeedVisit } from '@/lib/news/feed-seen';
import { isFeedItemUnread } from '@/lib/news/feed-unread';
import { NewsFeed } from '@/components/news/news-feed';
import { NewsTabs } from '@/features/news-tabs/news-tabs';
import { FeedRefreshButton } from '@/features/news-refresh/feed-refresh-button';
import { PullToRefreshZone } from '@/features/news-refresh/pull-to-refresh-zone';
import { useFeedAutoRefresh } from '@/features/news-refresh/use-feed-auto-refresh';
import type { FeedItem } from '@/components/news/types';

// Le Curieux n'a pas de niveau fixe : le niveau Live School est celui qu'il
// révise, mémorisé sur l'appareil et partagé avec l'Accueil et JeJoue.
export function PlayerNewsPage() {
  const { user } = useAuth();
  const userId = user?.id ?? '';

  const [level, setLevelState] = useState<SchoolLevel | null>(() => readStoredPlayerLevel());
  function setLevel(next: SchoolLevel) {
    setLevelState(next);
    storePlayerLevel(next);
  }

  const {
    items,
    authorsById,
    isLoading,
    hasMore,
    isFetchingMore,
    loadMore,
    refresh,
    isRefreshing,
  } = useRoleNewsFeed('player');
  const seenAtAtOpen = useFeedVisit();
  const isUnread = (item: FeedItem) =>
    seenAtAtOpen !== undefined && isFeedItemUnread(item, seenAtAtOpen, userId);

  useFeedAutoRefresh(refresh);

  return (
    <div className="pb-safe mx-auto max-w-2xl px-4 py-6 sm:py-8">
      <div className="mb-6">
        <div className="flex items-start justify-between gap-3">
          <h1 className="font-display text-ink-900 text-2xl font-extrabold">Feed</h1>
          <FeedRefreshButton onRefresh={refresh} isRefreshing={isRefreshing} />
        </div>
        <p className="text-ink-500 mt-1 font-sans text-sm">
          Tes défis partagés, le Live School et les classements.
        </p>
      </div>

      <NewsTabs
        userId={userId}
        level={level}
        onLevelChange={setLevel}
        actus={
          <PullToRefreshZone onRefresh={refresh} isRefreshing={isRefreshing}>
            <NewsFeed
              items={items}
              isLoading={isLoading}
              hasMore={hasMore}
              isFetchingMore={isFetchingMore}
              onLoadMore={loadMore}
              viewerRole="player"
              viewerId={userId}
              authorsById={authorsById}
              isUnread={isUnread}
            />
          </PullToRefreshZone>
        }
      />
    </div>
  );
}
